/**
 * useMessagingWindow — deriva o estado do composer do chat a partir da janela
 * de mensageria da Meta (24h desde a última mensagem do cliente).
 *
 * Consome `ConversationResponse.messagingWindow` (populado só nas leituras que
 * hidratam `provider` via aggregation — GET /conversations e GET /conversations/:id;
 * ausente em respostas de mutação como PUT /conversations/:id).
 *
 * Task 13 (faixa de aviso) e Task 14 (bloqueio do composer + reconciliação via
 * socket MESSAGE_STATUS) consomem este hook.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { MESSAGING_WINDOW_WARNING_HOURS, META_WINDOW_CLOSED_FAILED_CODES } from '@troia-v3/types';
import type { ConversationResponse } from '@troia-v3/types';

export type ComposerState = 'closed' | 'waiting' | 'window_closed' | 'contact_blocked' | 'active' | 'inactive';

const WARNING_MS = MESSAGING_WINDOW_WARNING_HOURS * 60 * 60 * 1000;
const TICK_MS = 60 * 1000;

/**
 * Códigos que significam "janela fechada" em `ConversationMessage.failedCode`.
 *
 * Movido pra `@troia-v3/types` (`META_WINDOW_CLOSED_FAILED_CODES`, review
 * 2026-07-27) — antes os dois números viviam hardcoded aqui, duplicados à mão
 * de `TROIA-V3-BACKEND/src/shared/messaging-window/errors.ts`, o exato tipo de
 * drift que a §5.1 da spec já tinha movido as outras constantes de janela pra
 * types pra evitar.
 *
 * - `131047` — WhatsApp Cloud API, código real do Graph (`WHATSAPP_WINDOW_CODE`).
 * - `-1` — sentinela `META_WINDOW_CLOSED_CUSTOMER_ONLY_CODE`. NÃO é um código
 *   real do Graph. Instagram e Messenger não têm um código numérico isolado
 *   equivalente (a Meta sinaliza os dois com o mesmo `code: 10` genérico,
 *   diferenciados só por subcode — `2534022`/`2018278` — que o backend já
 *   resolve via `isMetaWindowError` antes de gravar `failedCode`). Sem este
 *   valor no set, a reconciliação nunca dispara para Instagram/Messenger.
 *
 * Só o code puro: os pares com subcode do IG/Messenger não chegam ao front,
 * o backend já os converte em `failedCode`.
 *
 * Exportado porque `ChatMessage.tsx` também consome (Task 14 Step 6).
 */
export const META_WINDOW_CODES = new Set<number>(META_WINDOW_CLOSED_FAILED_CODES);

/**
 * `>= 1h` → "1h47" (piso) · `< 1h` → "47 min" · `< 1min` → "menos de 1 minuto"
 */
export function formatRemaining(ms: number): string {
  if (ms < 60 * 1000) return 'menos de 1 minuto';
  const totalMin = Math.floor(ms / (60 * 1000));
  if (totalMin < 60) return `${totalMin} min`;
  const h = Math.floor(totalMin / 60);
  const m = totalMin % 60;
  return `${h}h${String(m).padStart(2, '0')}`;
}

export interface UseMessagingWindowResult {
  state: ComposerState;
  showWarning: boolean;
  remainingLabel: string;
  reopenWith: 'template' | 'customer_only' | undefined;
}

export function useMessagingWindow(
  conversation: ConversationResponse | undefined,
  lastMessage?: { status?: string; failedCode?: number },
): UseMessagingWindowResult {
  // Tick que recalcula o ESTADO (não só o label) — o setTimeout e o intervalo
  // não são sincronizados, e sem isso o contador chegaria a "0h00" com a
  // sendbar ainda liberada.
  const [now, setNow] = useState(() => Date.now());
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const window_ = conversation?.messagingWindow;
  const expiresAtMs = useMemo(
    () => (window_?.expiresAt ? new Date(window_.expiresAt).getTime() : undefined),
    [window_?.expiresAt],
  );

  // Agenda a virada exata (fechamento) e o início do aviso.
  useEffect(() => {
    if (timeoutRef.current !== null) clearTimeout(timeoutRef.current);
    if (expiresAtMs === undefined) return;

    const agora = Date.now();
    const alvo = agora < expiresAtMs - WARNING_MS ? expiresAtMs - WARNING_MS : expiresAtMs;
    const delay = alvo - agora;
    if (delay <= 0) return;

    timeoutRef.current = setTimeout(() => setNow(Date.now()), delay);
    return () => {
      if (timeoutRef.current !== null) clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    };
  }, [expiresAtMs, now]);

  // Enquanto o aviso está visível, atualiza o label por minuto.
  const dentroDoAviso =
    expiresAtMs !== undefined && now < expiresAtMs && expiresAtMs - now <= WARNING_MS;

  useEffect(() => {
    if (intervalRef.current !== null) clearInterval(intervalRef.current);
    if (!dentroDoAviso) return;

    intervalRef.current = setInterval(() => setNow(Date.now()), TICK_MS);
    return () => {
      if (intervalRef.current !== null) clearInterval(intervalRef.current);
      intervalRef.current = null;
    };
  }, [dentroDoAviso]);

  // Reconciliação: a Meta é a autoridade. Se a última mensagem falhou com código
  // de janela, força fechado mesmo que nosso relógio discorde. Cobre tanto o
  // caminho assíncrono (Meta recusou via webhook) quanto o SÍNCRONO (Instagram/
  // Messenger rejeitam na própria resposta do Send API) — desde o fix B6
  // (review 2026-07-27), `sendMessage` também emite `MESSAGE_STATUS` no catch
  // de `sendViaProvider` quando o provider rejeita sincronamente (antes só
  // gravava `failed` no Mongo sem emitir socket). O gate do PRÓPRIO backend
  // (pré-persistência, 422 da Task 9) continua sem emitir socket — não há
  // mensagem criada pra reconciliar; esse caminho é descoberto pelo 422
  // (Task 14) via `handleMessagingWindowClosedError`, não por aqui.
  const falhouPorJanela =
    lastMessage?.status === 'failed' &&
    lastMessage.failedCode !== undefined &&
    META_WINDOW_CODES.has(lastMessage.failedCode);

  // Definição ÚNICA de "janela fechada" (spec §5.7): `isOpen` do servidor é só o
  // valor inicial — numa conversa aberta há 23h ele chega `true` e o relógio
  // local fecha antes de qualquer refetch.
  const janelaFechada =
    window_ !== undefined &&
    (window_.isOpen === false || (expiresAtMs !== undefined && now >= expiresAtMs) || falhouPorJanela);

  const state: ComposerState = (() => {
    if (!conversation) return 'inactive';
    // 2026-09-01: bloqueio vence a janela da Meta. Oferecer "envie um template"
    // para um contato bloqueado seria oferecer um caminho que o servidor recusa.
    if (conversation.contact?.blocked === true) return 'contact_blocked';
    if (conversation.status === 'closed') return 'closed';
    if (conversation.status === 'waiting') return 'waiting';
    if (conversation.status === 'active') return janelaFechada ? 'window_closed' : 'active';
    return 'inactive';
  })();

  // Invariante: a faixa fica ACIMA da sendbar — sem sendbar, sem faixa.
  const showWarning = state === 'active' && dentroDoAviso;

  return {
    state,
    showWarning,
    remainingLabel: expiresAtMs !== undefined ? formatRemaining(Math.max(0, expiresAtMs - now)) : '',
    reopenWith: window_?.reopenWith,
  };
}
